import { animated, easings, useTrail } from '@react-spring/web';
import classNames from 'classnames';
import type { FunctionComponent } from 'mdx/types';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useDidMount } from '../../../lib/hooks/useDidMount';
import { Chevron } from '../../icons/chevron';
import { Socials } from '../../socials';
import { Text } from '../../text';
import { Container } from '../grid';
import type { NavigationLinks } from '../navigation-links';
import styles from './sidebar.module.scss';
export const Sidebar: FunctionComponent<Props> = ({
  open,
  links,
  onOverlayClick
}) => {
  const router = useRouter();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [openSubItem, setOpenSubItem] = useState<number>(-1);
  const mounted = useDidMount();
  const trail = useTrail(links.length, {
    config: {
      duration: 150,
      easing: easings?.easeInOutBounce
    },
    delay: 200,
    scale: open ? 1 : 0.9,
    opacity: open ? 1 : 0,
    x: open ? 0 : 10
  });
  useEffect(() => {
    if (!open) {
      setOpenSubItem(-1);
    }
  }, [open]);
  const handleOverlayClick = (e: React.MouseEvent<HTMLElement>) => {
    if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
      onOverlayClick(false);
    }
  };
  const handleLinkClick = (e: React.MouseEvent<HTMLElement>, index: number) => {
    if (links[index]?.items?.length as number > 0) e.preventDefault();
    if (index === openSubItem) {
      setOpenSubItem(-1);
    } else {
      setOpenSubItem(index);
    }
  };
  return <>
            <nav role='navigation' ref={menuRef} id='menu' className={classNames(styles.menu, open && styles['menu-open'], mounted && styles.mounted)}>
                <Container className={styles['menu-inner']} data-sentry-element="Container" data-sentry-source-file="sidebar.tsx">
                    <ul className={styles.list}>
                        {links.map((route, index) => {
            const {
              label,
              href,
              items
            } = route;
            const isActive = router.pathname === href;
            const hasChildren = items && items?.length > 0;
            const subItemsOpen = openSubItem === index;
            return <animated.li key={href} style={trail[index]}>
                                    <Link href={href} passHref legacyBehavior>
                                        <a onClick={e => handleLinkClick(e, index)} className={classNames(styles.link, isActive && styles['link-active'])}>
                                            <Text typeStyle='heading-md' tag='div'>
                                                {label}
                                            </Text>

                                            {hasChildren && <Chevron direction={subItemsOpen ? 'up' : 'down'} color='currentColor' width='14' height='14' />}
                                        </a>
                                    </Link>
                                    {hasChildren && subItemsOpen && <ul className={classNames(styles['sub-items-list'])}>
                                            {items.map((route, index) => {
                  const {
                    label,
                    href
                  } = route;
                  const isActive = router.pathname === href;
                  return <animated.li key={href} style={trail[index]}>
                                                        <Link href={href} passHref legacyBehavior>
                                                            <a className={classNames(styles.link, isActive && styles['link-active'])}>
                                                                <Text typeStyle='heading-xs' tag='div'>
                                                                    {label}
                                                                </Text>
                                                            </a>
                                                        </Link>
                                                    </animated.li>;
                })}
                                        </ul>}
                                </animated.li>;
          })}
                    </ul>

                    <Socials className={styles.socials} data-sentry-element="Socials" data-sentry-source-file="sidebar.tsx" />
                </Container>
            </nav>

            {mounted && <div data-testid='sidebar.overlay' className={classNames(styles.overlay, mounted && styles.mounted, open && styles['overlay-active'])} onClick={handleOverlayClick} />}
        </>;
};
export default Sidebar;
interface Props {
  open: boolean;
  links: NavigationLinks;
  onOverlayClick: (open: boolean) => void;
}